// The "Booking to voyage completion" demo: Elariom coordinating one inbound raw-material shipment
// for a shipper's logistics team, on email and Teams, when the plan breaks. Scripted, so it plays
// the same every time: the supplier side (forwarder, supplier, truckers) and the customer side run
// in parallel, the extra cost is approved by the logistics team, and every date turns green.
//
// Based on a real exchange (two weeks, about 60 emails), anonymised and retold for a shipper.

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faEnvelope, faTruck, faIndustry, faShip, faCheck, faRotateRight, faPlay, faPause, faUsers, faBuilding, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { Pill } from './ElariomParts.js';
import ElariomCoordinationEmails from './ElariomCoordinationEmails.js';
import STORY2 from './ElariomCoordinationStory2.js';
import STORY3 from './ElariomCoordinationStory3.js';
import { INK, MUTED, HAIRLINE, SURFACE, GOOD, WARN, BAD, SERIF } from './elariomTheme.js';

const ELARIOM = 'Elariom · Logistics';

const SHIPMENT = {
    ref: 'PO 26-0417',
    what: 'Raw material · 2 × 40′ HC',
    route: 'Mainfeld Aromen, Alzenau (DE) → Hamburg → Valencia plant',
    deadline: 'Needed at the Valencia plant by 4 Nov for a contract production run',
};

// The threads, by side. Each is one email thread (or the Teams chat with the logistics team).
const THREADS = {
    forwarder: { side: 'supplier', icon: faShip, title: 'Hanse Freight · forwarder, Hamburg', subject: 'PO 26-0417 · Alzenau → Valencia · booking CMA CGM' },
    truckers: { side: 'supplier', icon: faTruck, title: 'Truckers near Alzenau', subject: 'Pickup Alzenau → Hamburg by 20 Oct · 2 × 40′ HC' },
    supplier: { side: 'supplier', icon: faIndustry, title: 'Mainfeld Aromen · raw-material supplier', subject: 'PO 26-0417 · new loading date' },
    team: { side: 'team', icon: faMicrosoft, title: 'Logistics team · Teams', subject: 'Approvals' },
    customer: { side: 'customer', icon: faBuilding, title: 'Nordic Beverages · customer', subject: 'Order SO-48117 · delivery week 46' },
};

// The script: who writes on which thread, at what (shown) time. `at` is seconds into the replay.
const SCRIPT = [
    { at: 0.4, thread: 'customer', from: 'Ingrid · Nordic Beverages', time: 'Mon 07:40', body: 'Hi, quick check: is order SO-48117 still on track for delivery in week 46?' },
    { at: 1.6, thread: 'forwarder', from: 'Anke · Hanse Freight', time: 'Mon 07:55', body: 'CMA has cancelled the rail from Alzenau. Truck only is about EUR 300 more per container, and we can no longer make the 23 Oct departure. How do you want to proceed?', alert: true, status: { sail: 'risk' } },
    { at: 3.4, thread: 'forwarder', from: ELARIOM, time: 'Mon 07:57', body: 'Please ask CMA for a direct truck that still makes the Hamburg cut-off on 20 Oct. I am checking other truckers in parallel.' },
    { at: 4.6, thread: 'truckers', from: ELARIOM, time: 'Mon 07:58', body: 'To 3 truckers: can you collect 2 × 40′ HC at Alzenau on or before 20 Oct for Hamburg (cut-off 20 Oct 19:30)? Price incl. empty pick-up, please.', fanout: 3 },
    { at: 6.2, thread: 'truckers', from: 'Spedition Kraus', time: 'Mon 08:21', body: 'Earliest is 22 Oct, sorry.', no: true },
    { at: 7.0, thread: 'forwarder', from: 'Anke · Hanse Freight', time: 'Mon 08:30', body: 'CMA can only collect by direct truck on 22 Oct. The yard closes on 20 Oct, so we would miss the vessel again.', no: true },
    { at: 7.9, thread: 'truckers', from: 'MainCargo', time: 'Mon 08:34', body: 'No capacity this week.', no: true },
    { at: 9.0, thread: 'truckers', from: 'Elbe Trucking', time: 'Mon 08:46', body: 'Yes: 20 Oct at 08:00, EUR 1,250 per container incl. empty pick-up at the inland depot.', yes: true, status: { pickup: 'pending' } },
    { at: 10.4, thread: 'supplier', from: ELARIOM, time: 'Mon 08:48', body: 'Can you load the 2 × 40′ HC on Tue 20 Oct at 08:00 instead of 16 Oct? Same goods, same documents. Elbe Trucking collects.' },
    { at: 11.8, thread: 'supplier', from: 'Jonas · Mainfeld Aromen', time: 'Mon 09:12', body: 'Yes, dock 2 at 08:00 works. Please send the truck plates the day before.', yes: true, status: { pickup: 'ok' } },
    { at: 13.0, thread: 'team', from: ELARIOM, time: 'Mon 09:13', approval: true, body: 'Keep the 24 Oct sailing with a direct truck: EUR 1,250 instead of EUR 895 per container, +EUR 710 for both. The raw material still reaches Valencia on 3 Nov, before the 4 Nov run.' },
    { at: 14.6, thread: 'team', from: 'Logistics manager', time: 'Mon 09:20', body: 'Approved.', yes: true },
    { at: 15.6, thread: 'forwarder', from: ELARIOM, time: 'Mon 09:21', body: 'Approved on our side. Please book Elbe Trucking for 20 Oct 08:00 and keep CMA CGM Aquila, sailing 24 Oct.' },
    { at: 17.0, thread: 'forwarder', from: 'Anke · Hanse Freight', time: 'Mon 09:40', body: 'Booked. Pick-up 20 Oct 08:00, same vessel, sailing 24 Oct, ETA Valencia 3 Nov.', yes: true, status: { cutoff: 'ok', sail: 'ok', eta: 'ok' } },
    { at: 18.2, thread: 'supplier', from: ELARIOM, time: 'Mon 09:41', body: 'Confirmed for 20 Oct 08:00, dock 2. Truck plates follow on Monday.' },
    { at: 19.2, thread: 'customer', from: ELARIOM, time: 'Mon 09:42', body: 'Yes, on track. The raw material arrives on 3 Nov, production runs 4–6 Nov, and delivery to you is 12 Nov as planned.', yes: true, status: { run: 'ok' } },
    { at: 20.4, thread: 'customer', from: 'Ingrid · Nordic Beverages', time: 'Mon 09:50', body: 'Great, thank you.' },
];
const END = 21.6;

// The dates that matter, turning green as each is confirmed.
const MILESTONES = [
    { id: 'pickup', label: 'Pick-up at the supplier', date: 'Tue 20 Oct · 08:00' },
    { id: 'cutoff', label: 'Hamburg cut-off', date: 'Tue 20 Oct · 19:30' },
    { id: 'sail', label: 'Sails · CMA CGM Aquila', date: 'Sat 24 Oct' },
    { id: 'eta', label: 'Arrives at the Valencia plant', date: 'Tue 3 Nov' },
    { id: 'run', label: 'Customer delivery', date: '12 Nov · week 46' },
];

const SIDES = [
    { id: 'supplier', title: 'Supplier side', threads: ['forwarder', 'truckers', 'supplier'] },
    { id: 'team', title: 'Your logistics team', threads: ['team'] },
    { id: 'customer', title: 'Customer side', threads: ['customer'] },
];

const BRAND = '#5A54E0';

function MilestoneStrip({ status }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${MILESTONES.length}, minmax(0, 1fr))`, gap: '8px' }}>
            {MILESTONES.map(m => {
                const st = status[m.id];
                const color = st === 'ok' ? GOOD : st === 'risk' ? BAD : st === 'pending' ? WARN : MUTED;
                return (
                    <Box key={m.id} sx={{ border: `1px solid ${st ? alpha(color, 0.5) : HAIRLINE}`, background: st ? alpha(color, 0.06) : '#FFFFFF', borderRadius: '8px', padding: '8px 12px', transition: 'all .4s' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', letterSpacing: '0.06em', textTransform: 'uppercase', color }}>
                            {st === 'ok' ? <FontAwesomeIcon icon={faCheck} style={{ fontSize: '10px' }} /> : st === 'risk' ? <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '10px' }} /> : <Box sx={{ width: '7px', height: '7px', borderRadius: '50%', border: `1.5px solid ${color}` }} />}
                            {m.label}
                        </Box>
                        <Box sx={{ fontSize: '14px', fontWeight: 700, marginTop: '2px', color: st === 'risk' ? BAD : INK, textDecoration: st === 'risk' ? 'line-through' : 'none' }}>{st === 'risk' ? 'Fri 23 Oct' : m.date}</Box>
                    </Box>
                );
            })}
        </Box>
    );
}

function Message({ m, fresh }) {
    const mine = m.from === ELARIOM;
    const accent = m.no ? BAD : m.yes ? GOOD : m.alert ? BAD : mine ? BRAND : HAIRLINE;
    return (
        <Box sx={{
            border: `1px solid ${mine ? alpha(BRAND, 0.35) : HAIRLINE}`, borderLeft: `3px solid ${accent}`, borderRadius: '8px',
            background: mine ? alpha(BRAND, 0.05) : '#FFFFFF', padding: '8px 11px', display: 'flex', flexDirection: 'column', gap: '3px',
            animation: fresh ? 'coordIn .45s ease-out' : 'none',
            '@keyframes coordIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '11px' }}>
                <Box component='span' sx={{ fontWeight: 700, color: mine ? BRAND : INK }}>{m.from}</Box>
                {m.fanout && <Pill color={BRAND}>sent to {m.fanout}</Pill>}
                <Box component='span' sx={{ marginLeft: 'auto', color: MUTED, whiteSpace: 'nowrap' }}>{m.time}</Box>
            </Box>
            <Box sx={{ fontSize: '12.5px', lineHeight: 1.45, color: INK }}>{m.body}</Box>
            {m.approval && (
                <Box sx={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <Box sx={{ fontSize: '11.5px', fontWeight: 600, padding: '3px 10px', borderRadius: '6px', background: INK, color: '#FFFFFF' }}>Approve</Box>
                    <Box sx={{ fontSize: '11.5px', padding: '3px 10px', borderRadius: '6px', border: `1px solid ${HAIRLINE}` }}>Look for cheaper</Box>
                </Box>
            )}
        </Box>
    );
}

function ThreadCard({ id, messages, active, lastIndex }) {
    const t = THREADS[id];
    const list = useRef(null);
    useEffect(() => { const el = list.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }, [messages.length]);
    return (
        <Box sx={{ border: `1px solid ${active ? INK : HAIRLINE}`, borderRadius: '10px', background: SURFACE, display: 'flex', flexDirection: 'column', minHeight: 0, transition: 'border-color .3s', flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderBottom: `1px solid ${HAIRLINE}`, minWidth: 0 }}>
                <FontAwesomeIcon icon={t.icon} style={{ fontSize: '12px', color: MUTED }} />
                <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <Box sx={{ fontSize: '12.5px', fontWeight: 700 }}>{t.title}</Box>
                    <Box sx={{ fontSize: '11px', color: MUTED, display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <FontAwesomeIcon icon={id === 'team' ? faUsers : faEnvelope} style={{ fontSize: '9px' }} /> {t.subject}
                    </Box>
                </Box>
            </Box>
            <Box ref={list} sx={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', minHeight: 0, flex: 1 }}>
                {!messages.length && <Box sx={{ fontSize: '11.5px', color: alpha(INK, 0.35) }}>Nothing yet</Box>}
                {messages.map(m => <Message key={m.i} m={m} fresh={m.i === lastIndex} />)}
            </Box>
        </Box>
    );
}

function DeskView({ open, onClose, tabs }) {
    const [ t, setT ] = useState(0);
    const [ playing, setPlaying ] = useState(false);
    const [ speed, setSpeed ] = useState(1);
    const last = useRef(null);

    useEffect(() => { if (open) { setT(0); setPlaying(true); } }, [open]);
    useEffect(() => {
        if (!playing) return undefined;
        last.current = performance.now();
        let raf;
        const tick = now => {
            const dt = (now - last.current) / 1000;
            last.current = now;
            setT(x => {
                const next = Math.min(END, x + dt * speed);
                if (next >= END) setPlaying(false);
                return next;
            });
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playing, speed]);

    const shown = SCRIPT.map((m, i) => ({ ...m, i })).filter(m => m.at <= t);
    const lastIndex = shown.length ? shown[shown.length - 1].i : -1;
    const activeThread = shown.length ? shown[shown.length - 1].thread : null;
    const status = shown.reduce((s, m) => ({ ...s, ...(m.status || {}) }), {});
    const done = t >= END;
    const sentByElariom = shown.filter(m => m.from === ELARIOM).reduce((n, m) => n + (m.fanout || 1), 0);

    return (
        <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { background: '#F6F5F1' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '18px 26px', gap: '14px', boxSizing: 'border-box' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography sx={{ fontFamily: SERIF, fontSize: '26px', fontWeight: 600 }}>Coordination</Typography>
                            <Pill color={MUTED} dashed>Demo</Pill>
                            {tabs}
                            {done
                                ? <Pill color={GOOD} dot>Back on plan · same sailing</Pill>
                                : status.sail === 'risk' ? <Pill color={BAD} dot>Plan broken · re-planning</Pill> : null}
                        </Box>
                        <Box sx={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>
                            <b style={{ color: INK }}>{SHIPMENT.ref}</b> · {SHIPMENT.what} · {SHIPMENT.route}
                        </Box>
                        <Box sx={{ fontSize: '12px', color: MUTED }}>{SHIPMENT.deadline}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box sx={{ fontSize: '11.5px', color: MUTED, textAlign: 'right', marginRight: '6px' }}>
                            <b style={{ color: INK }}>{sentByElariom}</b> emails sent by Elariom<br />0 phone calls
                        </Box>
                        <Button size='small' variant='outlined' onClick={() => setSpeed(speed === 1 ? 2 : 1)} sx={{ textTransform: 'none', minWidth: 0 }}>{speed}×</Button>
                        {done
                            ? <Button size='small' variant='contained' disableElevation startIcon={<FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '10px' }} />} onClick={() => { setT(0); setPlaying(true); }} sx={{ textTransform: 'none' }}>Replay</Button>
                            : <Button size='small' variant='contained' disableElevation startIcon={<FontAwesomeIcon icon={playing ? faPause : faPlay} style={{ fontSize: '10px' }} />} onClick={() => setPlaying(!playing)} sx={{ textTransform: 'none' }}>{playing ? 'Pause' : 'Play'}</Button>}
                        <IconButton onClick={onClose} aria-label='Close'><FontAwesomeIcon icon={faXmark} /></IconButton>
                    </Box>
                </Box>
                <MilestoneStrip status={status} />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2.1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '14px', flex: 1, minHeight: 0 }}>
                    {SIDES.map(side => (
                        <Box key={side.id} sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0, minWidth: 0 }}>
                            <Box sx={{ fontSize: '10.5px', letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>{side.title}</Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: side.threads.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr', gridTemplateRows: side.threads.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: '10px', flex: 1, minHeight: 0 }}>
                                {side.threads.map((id, k) => (
                                    <Box key={id} sx={{ display: 'flex', minHeight: 0, minWidth: 0, gridRow: side.threads.length > 1 && k === 0 ? 'span 2' : 'auto' }}>
                                        <ThreadCard id={id} messages={shown.filter(m => m.thread === id)} active={activeThread === id && !done} lastIndex={lastIndex} />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ fontSize: '11px', color: MUTED }}>Based on a real exchange of about 60 emails over two weeks, anonymised. Here it is resolved in under two hours, on the threads where each conversation already is.</Box>
            </Box>
        </Dialog>
    );
}

function ViewTabs({ view, setView }) {
    return (
        <Box sx={{ display: 'inline-flex', border: `1px solid ${HAIRLINE}`, borderRadius: '6px', overflow: 'hidden', marginLeft: '6px' }}>
            {[['emails', 'Emails'], ['desk', 'Desk']].map(([id, label]) => (
                <Box key={id} role='button' tabIndex={0} onClick={() => setView(id)} onKeyDown={e => e.key === 'Enter' && setView(id)}
                    sx={{ fontSize: '12px', padding: '3px 12px', cursor: 'pointer', background: view === id ? INK : '#FFFFFF', color: view === id ? '#FFFFFF' : INK }}>{label}</Box>
            ))}
        </Box>
    );
}

// Two views of the same story: the emails as the parties see them, and the desk for the team.
export default function ElariomCoordinationDemo({ open, onClose }) {
    const [ view, setView ] = useState('emails');
    const [ story, setStory ] = useState(1);
    const [ playing, setPlaying ] = useState(true);
    const [ speed, setSpeed ] = useState(1);
    const [ runKey, setRunKey ] = useState(0);
    const [ done, setDone ] = useState(false);
    useEffect(() => { if (open) { setRunKey(k => k + 1); setPlaying(true); setDone(false); } }, [open]);
    const tabs = <ViewTabs view={view} setView={setView} />;
    if (view === 'desk') return <DeskView open={open} onClose={onClose} tabs={tabs} />;
    return (
        <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { background: '#F6F5F1' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 22px' }}>
                    <Typography sx={{ fontFamily: SERIF, fontSize: '24px', fontWeight: 600 }}>Coordination</Typography>
                    <Pill color={MUTED} dashed>Demo</Pill>
                    {tabs}
                    <Box sx={{ display: 'inline-flex', border: `1px solid ${HAIRLINE}`, borderRadius: '6px', overflow: 'hidden' }}>
                        {[[1, 'Demo 1'], [2, 'Demo 2 · real case'], [3, 'Demo 3']].map(([id, label]) => (
                            <Box key={id} role='button' tabIndex={0} onClick={() => { setStory(id); setDone(false); setRunKey(k => k + 1); setPlaying(true); }} onKeyDown={e => e.key === 'Enter' && setStory(id)}
                                sx={{ fontSize: '12px', padding: '3px 12px', cursor: 'pointer', whiteSpace: 'nowrap', background: story === id ? INK : '#FFFFFF', color: story === id ? '#FFFFFF' : INK }}>{label}</Box>
                        ))}
                    </Box>
                    <Box sx={{ fontSize: '12px', color: MUTED, marginLeft: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{story === 1 ? '3 x 20′ stainless steel billets · HS 721899 · Kapfenberg → Hamburg → Altamira · ex-works 4 Dec' : story === 2 ? '2 x 40ft HC office furniture · Germany → Hamburg → Marsaxlokk · needed by 4 Nov' : '2 x 40ft HC office furniture · Germany → Hamburg → Veracruz · needed by 12 Nov'}</Box>
                    <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Button size='small' variant='outlined' onClick={() => setSpeed(speed === 1 ? 2 : 1)} sx={{ textTransform: 'none', minWidth: 0 }}>{speed}×</Button>
                        {done
                            ? <Button size='small' variant='contained' disableElevation startIcon={<FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '10px' }} />} onClick={() => { setDone(false); setRunKey(k => k + 1); setPlaying(true); }} sx={{ textTransform: 'none' }}>Replay</Button>
                            : <Button size='small' variant='contained' disableElevation startIcon={<FontAwesomeIcon icon={playing ? faPause : faPlay} style={{ fontSize: '10px' }} />} onClick={() => setPlaying(!playing)} sx={{ textTransform: 'none' }}>{playing ? 'Pause' : 'Play'}</Button>}
                        <IconButton onClick={onClose} aria-label='Close'><FontAwesomeIcon icon={faXmark} /></IconButton>
                    </Box>
                </Box>
                <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                    <ElariomCoordinationEmails key={story} story={story === 2 ? STORY2 : story === 3 ? STORY3 : undefined} playing={playing} speed={speed} runKey={runKey} onDone={() => setDone(true)} />
                </Box>
            </Box>
        </Dialog>
    );
}
